/**
 * `@shared-lib/org-access` — who may see and change an organization's design
 * data. The one copy the web app and the Cloud Functions both import.
 *
 * Membership lives in the books' Postgres (`organization_members`); a Cloud
 * Function mirrors it onto the user's Firebase ID token as the `orgId` /
 * `orgRole` custom claims (functions/features/books-claims.js), and a project
 * or price book carries the organization that owns it in `orgId`. The
 * Firestore rules make the same comparison (`inCallerOrg` / `callerOrgCanEdit`
 * in web-app/rules/firestore.rules.*); they cannot import this, so keep the
 * two in step.
 *
 * Roles mirror the plan (`claude-docs/2026-09-15-contractor-books-data-model-plan.md`
 * §4): owner and office change design data; field, bookkeeper and viewer
 * only read it. Field staff also CONTRIBUTE what they bring back from the
 * site: they file new scans (and may retry processing their own scan until it
 * first builds), add gallery photos and write notes — but never edit,
 * reprocess or delete what is already there.
 *
 * Projects are the ORGANIZATION's: only owner and office members create them
 * (`canCreateProjects`), and they send field staff to a property by listing
 * the tech in `projects.assignedTo`. A field member's project list is their
 * assignments (`seesOnlyAssignedProjects`); a tech who could create projects
 * would own them — rename, share, delete — which is backwards for an operator.
 *
 * Every function takes the token's CLAIMS (`{ orgId, orgRole, … }`). The rules
 * read the token, so the app must too: reading anything else (the profile's
 * `users/{uid}.orgId`) would let the app offer an edit the database refuses
 * for the up-to-an-hour a claim lags a membership change.
 */

const ORG_ROLES = ['owner', 'office', 'field', 'bookkeeper', 'viewer'];
const ORG_EDITOR_ROLES = ['owner', 'office'];
const ORG_CONTRIBUTOR_ROLES = ['owner', 'office', 'field'];
/** Roles that create projects (and copies) — the organization's own staff. */
const ORG_PROJECT_CREATOR_ROLES = ['owner', 'office'];
/** Roles whose project list is their assignments (`projects.assignedTo`). */
const ORG_ASSIGNED_ONLY_ROLES = ['field'];

/** The claims' organization owns `doc` (a project or price book). */
const inOrg = (claims, doc) => {
  const orgId = claims?.orgId;
  return Boolean(orgId) && doc?.orgId === orgId;
};

/**
 * A contractor's account: one an admin marked (`users/{uid}.accountKind ===
 * 'contractor'`), or a member of a company (a non-empty `orgId` claim, which
 * the books' claim sync stamps). The books desk's own definition
 * (web-app/src/books/entitlements.js), without its build flag. Who may use
 * the assistant, on both sides.
 */
const isContractorAccount = (userDoc, claims) =>
  userDoc?.accountKind === 'contractor' ||
  (typeof claims?.orgId === 'string' && claims.orgId !== '');

/** `inOrg`, and the claims' role may change design data. */
const isOrgEditor = (claims, doc) =>
  inOrg(claims, doc) && ORG_EDITOR_ROLES.includes(claims.orgRole);

/**
 * `inOrg`, and the claims' role may add site material to `doc` (a project):
 * new scans, gallery photos and notes — what field staff bring back.
 */
const isOrgContributor = (claims, doc) =>
  inOrg(claims, doc) && ORG_CONTRIBUTOR_ROLES.includes(claims.orgRole);

/**
 * May create a project (or a copy of one). Anyone outside an organization
 * may — their projects are their own; inside one, only owner and office.
 * Mirrored by the Firestore rules' `projects` create.
 */
const canCreateProjects = (claims) =>
  !claims?.orgId || ORG_PROJECT_CREATOR_ROLES.includes(claims.orgRole);

/**
 * The user's list of the organization's projects is the ones they are
 * assigned to (`projects.assignedTo` contains their uid), not all of them.
 */
const seesOnlyAssignedProjects = (claims) =>
  Boolean(claims?.orgId) && ORG_ASSIGNED_ONLY_ROLES.includes(claims.orgRole);

/** `uid` is assigned to `project`. */
const isAssignedTo = (project, uid) =>
  Boolean(uid) &&
  Array.isArray(project?.assignedTo) &&
  project.assignedTo.includes(uid);

/**
 * What `uid` may do with `project`, as the Firestore rules for projects decide
 * it:
 *   'owner'  — its `ownerId`; the only level that may delete the project or
 *              change who owns it
 *   'editor' — an owner / office member of the organization that owns it;
 *              changes its contents
 *   'viewer' — shared with them (`viewerIds`), or a reading member of its
 *              organization
 *   'none'
 */
function projectAccessLevel(project, uid, claims) {
  if (!project || !uid) return 'none';
  if (project.ownerId === uid) return 'owner';
  if (isOrgEditor(claims, project)) return 'editor';
  if (project.viewerIds?.includes(uid) || inOrg(claims, project)) {
    return 'viewer';
  }
  return 'none';
}

/**
 * The fields that say who may reach a project. None of them may follow a
 * project into a COPY: the copy belongs to its new owner.
 */
const PROJECT_ACCESS_FIELDS = ['orgId', 'viewerIds', 'sharedWith'];

/**
 * `project` as a copy owned by someone whose organization is `ownerOrgId`
 * (their `users/{uid}.orgId` — what the web app stamps a new project with, via
 * `orgStampFor`; null / undefined / '' for none). Every access field of the
 * source is dropped, and `orgId` becomes the new owner's. Carrying the
 * source's made a signup's demo project, and an outside viewer's "Make a
 * Copy", a Team Project of the SOURCE owner's organization. Pure.
 */
function accessForNewOwner(project, ownerOrgId) {
  const copy = { ...project };
  for (const field of PROJECT_ACCESS_FIELDS) delete copy[field];
  if (ownerOrgId) copy.orgId = ownerOrgId;
  return copy;
}

/**
 * Whether a project is, or ever was, a demo copy. A demo belongs to no
 * organization: stamped, a member's tutorial project shows in every
 * colleague's Team Projects (see scripts/books/strip-demo-copy-org-ids.js).
 *
 * "Was ever", not "is the current one": resetting a sample project RETIRES
 * the old one by setting `isDemoProject: false` (`demo-project-copy.js` →
 * `supersedeProject`), so a flag-only check would stamp a project holding
 * whatever a member did during a lesson. `sourceDemoId` is written once at
 * copy time and never cleared, so it is the durable marker. Pure.
 */
const wasEverDemoCopy = (project) =>
  project?.isDemoProject === true || Boolean(project?.sourceDemoId);

/**
 * Which of a member's projects (`[{ id, data }]`) to stamp with their
 * organization `orgId`: every one not already in it, never a demo copy —
 * the one rule `backfill-org-ids.js` and a new company's founder share. Pure.
 */
const projectsToStamp = (projects, orgId) =>
  projects
    .filter(({ data }) => !wasEverDemoCopy(data) && data?.orgId !== orgId)
    .map(({ id }) => id);

/** Whether a level from `projectAccessLevel` may change a project's contents. */
const canEditAtLevel = (level) => level === 'owner' || level === 'editor';

module.exports = {
  ORG_ROLES,
  ORG_EDITOR_ROLES,
  ORG_CONTRIBUTOR_ROLES,
  ORG_PROJECT_CREATOR_ROLES,
  ORG_ASSIGNED_ONLY_ROLES,
  canCreateProjects,
  isContractorAccount,
  seesOnlyAssignedProjects,
  isAssignedTo,
  inOrg,
  isOrgEditor,
  isOrgContributor,
  projectAccessLevel,
  canEditAtLevel,
  PROJECT_ACCESS_FIELDS,
  accessForNewOwner,
  wasEverDemoCopy,
  projectsToStamp,
};
